import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import type { Page } from '@/shared/api/client';
import type { ApiError } from '@/shared/api/errors';
import { contactKeys, contactsApi } from '@/shared/contacts/api';
import type {
  Contact,
  ContactChange,
  ContactGroup,
  ContactGroupWrite,
  ContactWrite,
} from '@/shared/contacts/types';
import { emailsRootKey } from '@/shared/emails/api';
import { useElevation } from '@/shared/elevation/useElevation';
import { sessionQueryKey } from '@/shared/session/currentUser';
import {
  type Capability,
  FALLBACK_CAPABILITIES,
  type UserProfile,
} from '@/shared/session/profiles';
import {
  type Guideline,
  type GuidelineFilters,
  guidelineKeys,
  guidelinesApi,
  type GuidelineUpdate,
} from '@/shared/triage/guidelines';

import { adminApi, adminKeys } from './api';
import type {
  AccessLogFilters,
  AdminUser,
  AdminUserCreate,
  AdminUserCreated,
  AdminUserUpdate,
  ConnectionTest,
  DocumentRetentionPolicy,
  ErrorFilters,
  FeedbackAnswerFilters,
  FeedbackFilters,
  InviteRead,
  Mailbox,
  MailboxCredentials,
  MailboxUpdate,
  MailboxWrite,
  ModesWrite,
  ReliabilityFilters,
  RetentionRun,
  TriageActivation,
} from './types';

/**
 * What changes when a mailbox appears, goes, or is given to someone: which mail each
 * person sees. The inbox (its list, rail and mailbox filter) and the session (its list
 * of mailboxes) are read again, or a mailbox registered or assigned in the panel stays
 * invisible in the Bandeja until the page is reloaded. That happened in production on
 * 2026-09-24 with `ventas@`.
 */
const WHO_SEES_WHAT = [emailsRootKey, sessionQueryKey] as const;

export function useAdminUsers() {
  return useQuery({ queryKey: adminKeys.users, queryFn: adminApi.listUsers });
}

/**
 * The history of one person (contract v10.58). A back older than that answers 404 (or
 * 405): no retry, and the card simply leaves the section out.
 */
export function useUserHistory(id: string) {
  return useQuery({
    queryKey: adminKeys.userHistory(id),
    queryFn: () => adminApi.userHistory(id),
    retry: false,
  });
}

/** What each profile allows (contract v10.58); without it, the hints written here. */
export function useProfiles() {
  return useQuery({
    queryKey: adminKeys.profiles,
    queryFn: adminApi.profiles,
    retry: false,
    staleTime: Infinity,
  });
}

/**
 * What each profile allows: the API's table when the back sends it (contract v10.58), the
 * one written in `profiles.ts` otherwise.
 */
export function useProfileCapabilities(): Record<UserProfile, readonly Capability[]> {
  const profiles = useProfiles();
  if (!profiles.data) return FALLBACK_CAPABILITIES;
  const table: Record<UserProfile, readonly Capability[]> = { ...FALLBACK_CAPABILITIES };
  for (const each of profiles.data) table[each.profile] = each.capabilities;
  return table;
}

export function useAdminMailboxes() {
  return useQuery({ queryKey: adminKeys.mailboxes, queryFn: adminApi.listMailboxes });
}

export function useAdminTriage() {
  return useQuery({ queryKey: adminKeys.triage, queryFn: adminApi.triage });
}

export function useAccessLog(filters: AccessLogFilters) {
  return useQuery({
    queryKey: adminKeys.accessLog(filters),
    queryFn: () => adminApi.accessLog(filters),
    placeholderData: (previous) => previous,
  });
}

export function useRetentionPolicy() {
  return useQuery({ queryKey: adminKeys.documentPolicy, queryFn: adminApi.documentPolicy });
}

export function useRetention() {
  return useQuery({ queryKey: adminKeys.retention, queryFn: adminApi.retention });
}

export function useFeedback(filters: FeedbackFilters) {
  return useQuery({
    queryKey: adminKeys.feedback(filters),
    queryFn: () => adminApi.feedback(filters),
  });
}

export function useErrors(filters: ErrorFilters) {
  return useQuery({ queryKey: adminKeys.errors(filters), queryFn: () => adminApi.errors(filters) });
}

/** Administración → Fiabilidad (contract v10.37): the previous window stays on screen
 * while the next one is computed. */
export function useReliability(filters: ReliabilityFilters) {
  return useQuery({
    queryKey: adminKeys.reliability(filters),
    queryFn: () => adminApi.reliability(filters),
    placeholderData: (previous) => previous,
  });
}

/** The rows of one group, asked for when the group is opened, not before. */
export function useErrorOccurrences(fingerprint: string | null, since: string) {
  return useQuery({
    queryKey: adminKeys.errorOccurrences(fingerprint ?? '', since),
    queryFn: () => adminApi.errorOccurrences(fingerprint ?? '', since),
    enabled: Boolean(fingerprint),
  });
}

export function useFeedbackAnswers(filters: FeedbackAnswerFilters) {
  return useQuery({
    queryKey: adminKeys.feedbackAnswers(filters),
    queryFn: () => adminApi.feedbackAnswers(filters),
    placeholderData: (previous) => previous,
  });
}

export function useFeedbackMailboxes() {
  return useQuery({ queryKey: adminKeys.feedbackMailboxes, queryFn: adminApi.feedbackMailboxes });
}

/**
 * Every write of the panel: wrapped in `withElevation`, so a 403 `elevation_required`
 * becomes the password prompt and a retry, and the relevant list is refetched after.
 */
function useGuarded<TData, TVariables>(
  run: (variables: TVariables) => Promise<TData>,
  invalidate: readonly (readonly string[])[],
  onSuccess?: (data: TData) => void,
) {
  const { withElevation } = useElevation();
  const queryClient = useQueryClient();
  return useMutation<TData, ApiError | Error, TVariables>({
    mutationFn: (variables) => withElevation(() => run(variables)),
    onSuccess: (data) => {
      for (const key of invalidate) void queryClient.invalidateQueries({ queryKey: key });
      onSuccess?.(data);
    },
  });
}

export function useCreateUser(onSuccess?: (user: AdminUserCreated) => void) {
  return useGuarded<AdminUserCreated, AdminUserCreate>(
    adminApi.createUser,
    [adminKeys.users, ...WHO_SEES_WHAT],
    onSuccess,
  );
}

export function useInviteUser(onSuccess?: (invite: InviteRead) => void) {
  return useGuarded<InviteRead, string>(adminApi.inviteUser, [adminKeys.users], onSuccess);
}

/**
 * The answer of the `PATCH` goes into the list at once, before it is read again: the card
 * of the person compares its draft with the row, and with the old row for a moment it
 * would say «1 cambio sin guardar» right after saving. `last_seen_at` stays the list's;
 * the answers of the writes leave it empty (contract v10.58).
 */
export function useUpdateUser() {
  const { withElevation } = useElevation();
  const queryClient = useQueryClient();
  return useMutation<AdminUser, ApiError | Error, { id: string; payload: AdminUserUpdate }>({
    mutationFn: ({ id, payload }) => withElevation(() => adminApi.updateUser(id, payload)),
    onSuccess: (fresh) => {
      queryClient.setQueryData<Page<AdminUser>>(adminKeys.users, (list) =>
        list
          ? {
              ...list,
              items: list.items.map((each) =>
                each.id !== fresh.id
                  ? each
                  : each.last_seen_at === undefined
                    ? fresh
                    : { ...fresh, last_seen_at: each.last_seen_at },
              ),
            }
          : list,
      );
      for (const key of [adminKeys.users, ...WHO_SEES_WHAT])
        void queryClient.invalidateQueries({ queryKey: key });
    },
  });
}

export function useResetPassword(onSuccess?: (user: AdminUser) => void) {
  return useGuarded<AdminUser, { id: string; password: string }>(
    ({ id, password }) => adminApi.resetPassword(id, password),
    [adminKeys.users],
    onSuccess,
  );
}

/**
 * «Reiniciar doble factor» (contract v10.45). The session is read again too: resetting
 * one's own turns `two_factor_enabled` off in it.
 */
export function useResetTwoFactor(onSuccess?: (user: AdminUser) => void) {
  return useGuarded<AdminUser, string>(
    adminApi.resetTwoFactor,
    [adminKeys.users, sessionQueryKey],
    onSuccess,
  );
}

export function useTestMailbox() {
  return useGuarded<ConnectionTest, MailboxCredentials>(adminApi.testMailbox, []);
}

/**
 * «Probar conexión» of a registered mailbox (contract v10.17): nobody types the password
 * again. Tested as it is stored, the row's «Última prueba» and «Estado» change, so the
 * list is read again.
 */
export function useTestSavedMailbox() {
  return useGuarded<ConnectionTest, { id: string; typed?: MailboxUpdate }>(
    ({ id, typed }) => adminApi.testSavedMailbox(id, typed),
    [adminKeys.mailboxes],
  );
}

export function useCreateMailbox(onSuccess?: (mailbox: Mailbox) => void) {
  return useGuarded<Mailbox, MailboxWrite>(
    adminApi.createMailbox,
    [adminKeys.mailboxes, adminKeys.triage, ...WHO_SEES_WHAT],
    onSuccess,
  );
}

/**
 * A new name (contract v10.23) moves the mail, the people who see the mailbox and its
 * modes with it: the inbox, the users list and the session (its list of mailboxes) are
 * read again, or they would keep showing the old name until the next reload.
 */
export function useUpdateMailbox() {
  return useGuarded<Mailbox, { id: string; payload: MailboxUpdate }>(
    ({ id, payload }) => adminApi.updateMailbox(id, payload),
    [adminKeys.mailboxes, adminKeys.triage, adminKeys.users, ...WHO_SEES_WHAT],
  );
}

/** «Borrar definitivamente» (contract v10.14): the row and its modes go, the mail stays. */
export function useDeleteMailbox(onSuccess?: () => void) {
  return useGuarded<void, string>(
    adminApi.deleteMailbox,
    [adminKeys.mailboxes, adminKeys.triage, adminKeys.users, ...WHO_SEES_WHAT],
    onSuccess,
  );
}

/**
 * The triage writes answer with the whole activation as it is now, so the cache is set
 * from the answer instead of refetched. The read-only `/activacion` page keeps its own
 * key and is invalidated.
 */
function useTriageWrite<TVariables>(run: (variables: TVariables) => Promise<TriageActivation>) {
  const queryClient = useQueryClient();
  return useGuarded<TriageActivation, TVariables>(run, [], (data) => {
    queryClient.setQueryData(adminKeys.triage, data);
    void queryClient.invalidateQueries({ queryKey: ['triage', 'activation'] });
  });
}

export function usePutDestinations() {
  return useTriageWrite<Record<string, string[]>>(adminApi.putDestinations);
}

export function usePutFolders() {
  return useTriageWrite<Record<string, string>>(adminApi.putFolders);
}

export function usePutModes() {
  return useTriageWrite<ModesWrite>(adminApi.putModes);
}

export function useAllShadow() {
  return useTriageWrite<void>(() => adminApi.allShadow());
}

export function useStorageUsage() {
  return useQuery({ queryKey: adminKeys.storage, queryFn: adminApi.storage });
}

export function useMeasureStorage() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: adminApi.measureStorage,
    onSuccess: (usage) => queryClient.setQueryData(adminKeys.storage, usage),
  });
}

export function useRunRetention() {
  return useGuarded<RetentionRun, void>(
    () => adminApi.runRetention(),
    [adminKeys.retention, ['admin', 'access-log']],
  );
}

export function usePutRetentionPolicy() {
  const queryClient = useQueryClient();
  return useGuarded<DocumentRetentionPolicy, Record<string, number>>(
    adminApi.putDocumentPolicy,
    [],
    (policy) => queryClient.setQueryData(adminKeys.documentPolicy, policy),
  );
}

/**
 * «Lo que ha aprendido» (contract v10.35): one page of criteria with this status. The
 * previous page stays on screen while the next one loads, as in the access log.
 */
export function useGuidelines(filters: GuidelineFilters) {
  return useQuery({
    queryKey: guidelineKeys.list(filters),
    queryFn: () => guidelinesApi.list(filters),
    placeholderData: (previous) => previous,
  });
}

/**
 * Change a criterion or turn it off and on (contract v10.35). Not behind the password of
 * the elevated mode: the API does not ask for it (the author of a note turns their own off
 * from the email panel), and every change is still a line in the access log. The lists
 * and the counts are read again, and the email details, whose `note_learning` may name it.
 */
export function useUpdateGuideline() {
  const queryClient = useQueryClient();
  return useMutation<Guideline, ApiError, { id: string; change: GuidelineUpdate }>({
    mutationFn: ({ id, change }) => guidelinesApi.update(id, change),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: guidelineKeys.all });
      void queryClient.invalidateQueries({ queryKey: emailsRootKey });
    },
  });
}

/**
 * Delete a criterion for good (contract v10.36). Behind the password of the elevated mode,
 * like deleting a mailbox: the API asks for it, and there is no way back. The email details
 * are read again, because their «por qué» and `note_learning` named it.
 */
export function useDeleteGuideline(onSuccess?: () => void) {
  return useGuarded<void, string>(
    guidelinesApi.remove,
    [guidelineKeys.all, emailsRootKey],
    onSuccess,
  );
}

/**
 * The address book (contract v10.44, ADR 0038 of the back): adding, correcting and
 * deleting a contact is for whoever administers and asks for no password. The lists and
 * the suggestions of «Para» are read again.
 */
function useContactWrite<TVariables, TData = Contact>(
  run: (variables: TVariables) => Promise<TData>,
) {
  const queryClient = useQueryClient();
  return useMutation<TData, ApiError, TVariables>({
    mutationFn: run,
    onSuccess: () => void queryClient.invalidateQueries({ queryKey: contactKeys.all }),
  });
}

export function useCreateContact() {
  return useContactWrite<ContactWrite>(contactsApi.create);
}

export function useUpdateContact() {
  return useContactWrite<{ id: string; change: ContactChange }>(({ id, change }) =>
    contactsApi.update(id, change),
  );
}

export function useDeleteContact() {
  return useContactWrite<string, void>(contactsApi.remove);
}

/**
 * What a group changes besides itself: «Comerciales» is also the triage destination of that
 * name, so its addresses in Destinos and Activación, and what «Correcto» promises in an
 * email (`destinations_without_address`), are read again.
 */
const GROUP_REACH = [
  contactKeys.all,
  adminKeys.triage,
  ['triage', 'activation'],
  emailsRootKey,
] as const;

/** A group is written with the session elevated, like Destinos: one is a destination. */
export function useCreateGroup(onSuccess?: (group: ContactGroup) => void) {
  return useGuarded<ContactGroup, ContactGroupWrite>(
    contactsApi.createGroup,
    GROUP_REACH,
    onSuccess,
  );
}

export function useUpdateGroup(onSuccess?: (group: ContactGroup) => void) {
  return useGuarded<ContactGroup, { id: string; payload: ContactGroupWrite }>(
    ({ id, payload }) => contactsApi.updateGroup(id, payload),
    GROUP_REACH,
    onSuccess,
  );
}

export function useDeleteGroup(onSuccess?: () => void) {
  return useGuarded<void, string>(contactsApi.removeGroup, GROUP_REACH, onSuccess);
}
